import React, { useState, useEffect, useRef } from 'react';

interface UseRapidCounterReturn {
	currentValue: number;
	ref: React.RefObject<HTMLDivElement>;
}

interface UseRapidCounterProps {
	finalValue: number;
	duration: number;
	initialValue: number;
	intersectionThreshold: number;
}

const useRapidCounter = ({
	finalValue,
	duration,
	initialValue,
	intersectionThreshold
}: UseRapidCounterProps): UseRapidCounterReturn => {
	const [currentValue, setCurrentValue] = useState(initialValue);
	const elementRef = useRef<HTMLDivElement>(null);
	const animationFrameId = useRef<number>(0);
	const startTime = useRef<number | null>(null);
	const [isInView, setIsInView] = useState(false);
	const [animationStarted, setAnimationStarted] = useState(false);

	useEffect(() => {
		const currentElement = elementRef.current;

		if (!currentElement) {
			return;
		}

		const options = {
			root: null,
			rootMargin: '0px',
			threshold: intersectionThreshold,
		};

		const observer = new IntersectionObserver((entries) => {
			const [entry] = entries;
			setIsInView(entry.isIntersecting);
		}, options);

		observer.observe(currentElement);

		return () => {
			observer.unobserve(currentElement);
			observer.disconnect();
		};

	}, [intersectionThreshold]);

	useEffect(() => {
		if (isInView && !animationStarted) {
			setAnimationStarted(true);

			const animate = (timestamp: number) => {
				if (!startTime.current) {
					startTime.current = timestamp;
				}

				const elapsedTime = timestamp - startTime.current;
				const progress = Math.min(elapsedTime / duration, 1);

				const valueRange = finalValue - initialValue;
				const calculatedValue = initialValue + valueRange * progress;

				setCurrentValue(Math.round(calculatedValue));

				if (progress < 1) {
					animationFrameId.current = requestAnimationFrame(animate);
				}
			};

			animationFrameId.current = requestAnimationFrame(animate);
		}

		return () => {
			// cancelAnimationFrame(animationFrameId.current);
		};

	}, [isInView, finalValue, duration, initialValue, animationStarted]);

	return { currentValue, ref: elementRef };
};

export default useRapidCounter;
